import { useEffect, useState } from "react"
import { useFormik } from "formik"
import * as yup from "yup"
import { useDispatch } from "react-redux"
import { useLoginApiMutation } from "../Login/Services/loginService"
import { setCredentials } from "../Login/authSlice"
import { setLocationModal } from "../Common/commonSlice"
import i18n from "../../i18n"

/**
 * The survey lane's own login.
 *
 * This deliberately does NOT share `useLogin` with the commissioning lane.
 * Parameterising that hook by lane meant a change made for one lane silently
 * altered the other — the location guard in particular, which turned Verify
 * into a no-op on this screen. The two lanes now only share the HTTP endpoint,
 * which is genuinely shared server-side (handover §2.1).
 */
export const useSurveyLogin = () => {
    const dispatch = useDispatch()

    const [loginApi, { error: loginError, isLoading: isLoginLoading, isError: isLoginError }] =
        useLoginApiMutation()

    const [loader, setLoader] = useState(false)

    useEffect(() => {
        localStorage.setItem("lang", "en")
    }, [])

    const validationSchema = yup.object({
        name: yup.string().trim().required("Enter the surveyor name"),
        phone: yup
            .string()
            .required("Enter the surveyor number")
            .matches(/^[0-9]{10}$/, "Enter a valid 10-digit number"),
        // The site code is 6 characters and may contain letters as well as
        // digits, so it is uppercased rather than restricted to numerics.
        vendor_code: yup
            .string()
            .trim()
            .transform(value => (typeof value === "string" ? value.toUpperCase() : value))
            .required("Enter the site code")
            .matches(/^[A-Z0-9]{6}$/, "Enter the 6-character site code"),
    })

    const formik = useFormik({
        initialValues: { name: "", phone: "", vendor_code: "" },
        validationSchema,
        onSubmit: values => {
            const latitude = localStorage.getItem("latitude")
            const longitude = localStorage.getItem("longitude")

            // Location is granted on this screen before login; without it the
            // visit cannot be stamped, so prompt rather than submit.
            if (!latitude || !longitude) {
                dispatch(setLocationModal({ locationModal: true }))
                return
            }

            loginApi({
                ...values,
                vendor_code: values.vendor_code.trim().toUpperCase(),
                latitude: parseFloat(latitude) as any,
                longitude: parseFloat(longitude) as any,
            })
                .unwrap()
                .then((response: any) => {
                    setLoader(true)
                    if (response?.meta?.status_code === 200) {
                        const data = response?.data
                        // §2.1 returns only access_token / expires_at / flow —
                        // it does not echo the surveyor back. Page 4 shows their
                        // name and number, so keep what they typed here. Spread
                        // `data` last so the backend wins if it ever sends them.
                        localStorage.setItem(
                            window.btoa("user_info"),
                            window.btoa(
                                JSON.stringify({
                                    name: values.name.trim(),
                                    phone: values.phone.trim(),
                                    ...data,
                                })
                            )
                        )
                        i18n.changeLanguage("en")
                        dispatch(setCredentials({ token: data?.access_token }))
                    }
                })
                .catch(() => {
                    setLoader(true)
                })
        },
    })


    return {
        states: { formik, loader, loginError, isLoginError, isLoginLoading },
        functions: {},
    }
}

export default useSurveyLogin
